import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, RotateCcw, Server, Zap } from 'lucide-react';
import { Pagination } from '../../components/client';
import { useAdminFailedOperations } from '../../hooks/useAdminFailedOperations';
import { useToastContext } from '../../contexts/ToastContext';
import { formatDate, formatTime } from '../../utils/dateUtils';

const RetryButton = ({ onClick, disabled, label = 'Retry' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
  >
    <RotateCcw className="w-3.5 h-3.5" />
    {label}
  </button>
);

const EmptyRow = ({ message }) => (
  <tr>
    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
      {message}
    </td>
  </tr>
);

const AdminFailedOperations = () => {
  const [activeTab, setActiveTab] = useState('queue');
  const [pages, setPages] = useState({ queue: 1, outbox: 1, stripe: 1 });
  const toast = useToastContext();
  const {
    summary,
    perPage,
    queueJobs,
    queueMeta,
    outboxEvents,
    outboxMeta,
    stripeWebhooks,
    stripeMeta,
    isLoading,
    refetch,
    retryQueueJob,
    retryOutboxEvent,
    retryStripeWebhook,
    retryAllQueueJobs,
    retryAllOutboxEvents,
    retryAllStripeWebhooks,
    isRetrying,
  } = useAdminFailedOperations({
    queuePage: pages.queue,
    outboxPage: pages.outbox,
    stripePage: pages.stripe,
  });

  const activeMeta =
    activeTab === 'queue' ? queueMeta : activeTab === 'outbox' ? outboxMeta : stripeMeta;

  const handlePageChange = (page) => {
    setPages((current) => ({ ...current, [activeTab]: page }));
  };

  const tabs = [
    { id: 'queue', label: 'Queue jobs', count: summary?.queue_failed ?? 0, bulkLimit: 50 },
    { id: 'outbox', label: 'Outbox events', count: summary?.outbox_failed ?? 0, bulkLimit: 50 },
    { id: 'stripe', label: 'Stripe webhooks', count: summary?.stripe_failed ?? 0, bulkLimit: 25 },
  ];

  const activeTabConfig = tabs.find((t) => t.id === activeTab);

  const handleBulkRetry = () => {
    const tab = activeTabConfig;
    if (!tab || tab.count === 0) {
      return;
    }

    const limit = Math.min(tab.bulkLimit, tab.count);
    const confirmed = window.confirm(
      `Retry up to ${limit} failed ${tab.label.toLowerCase()}?`
    );
    if (!confirmed) {
      return;
    }

    const onSuccess = (response) => {
      const stats = response?.data || {};
      const message = response?.message || 'Bulk retry completed';
      if ((stats.failed ?? 0) > 0) {
        toast.error(null, `${message} (${stats.failed} failed)`);
      } else {
        toast.success(null, message);
      }
    };

    const onError = () => {
      toast.error(null, 'Bulk retry failed');
    };

    if (activeTab === 'queue') {
      retryAllQueueJobs.mutate(limit, { onSuccess, onError });
    } else if (activeTab === 'outbox') {
      retryAllOutboxEvents.mutate(limit, { onSuccess, onError });
    } else {
      retryAllStripeWebhooks.mutate(limit, { onSuccess, onError });
    }
  };

  const totalFailed =
    (summary?.queue_failed ?? 0) +
    (summary?.outbox_failed ?? 0) +
    (summary?.stripe_failed ?? 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-amber-600" />
            Failed operations
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Retry failed queue jobs, outbox publishes, and Stripe webhook processing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/performance"
            className="text-sm text-primary-600 hover:underline"
          >
            Infrastructure overview
          </Link>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`text-left rounded-lg border p-4 transition-colors ${
              activeTab === tab.id
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <p className="text-sm text-gray-600">{tab.label}</p>
            <p className={`text-2xl font-bold tabular-nums mt-1 ${tab.count > 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {tab.count}
            </p>
          </button>
        ))}
      </div>

      {totalFailed === 0 && !isLoading && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 flex items-center gap-2">
          <Server className="w-4 h-4" />
          No failed operations right now.
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-gray-500" />
            <h2 className="font-semibold text-gray-900">
              {activeTabConfig?.label}
            </h2>
          </div>
          {(activeTabConfig?.count ?? 0) > 0 && (
            <RetryButton
              label="Retry all"
              disabled={isRetrying}
              onClick={handleBulkRetry}
            />
          )}
        </div>

        {activeTab === 'queue' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Queue</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Failed at</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <EmptyRow message="Loading failed jobs…" />
                ) : queueJobs.length === 0 ? (
                  <EmptyRow message="No failed queue jobs." />
                ) : (
                  queueJobs.map((job) => (
                    <tr key={job.uuid}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{job.job_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{job.queue}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {job.failed_at ? (
                          <>
                            {formatDate(job.failed_at, 'medium')}
                            <span className="block text-xs text-gray-500">{formatTime(job.failed_at)}</span>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-red-700 max-w-xs truncate" title={job.exception}>
                        {job.exception || '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <RetryButton
                          disabled={isRetrying}
                          onClick={() => retryQueueJob.mutate(job.uuid)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'outbox' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Topic</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <EmptyRow message="Loading outbox events…" />
                ) : outboxEvents.length === 0 ? (
                  <EmptyRow message="No failed outbox events." />
                ) : (
                  outboxEvents.map((event) => (
                    <tr key={event.id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{event.event_type}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{event.topic}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 tabular-nums">{event.attempts}</td>
                      <td className="px-4 py-3 text-xs text-red-700 max-w-xs truncate" title={event.last_error}>
                        {event.last_error || '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <RetryButton
                          disabled={isRetrying}
                          onClick={() => retryOutboxEvent.mutate(event.id)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'stripe' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stripe event ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <EmptyRow message="Loading Stripe webhooks…" />
                ) : stripeWebhooks.length === 0 ? (
                  <EmptyRow message="No failed Stripe webhooks." />
                ) : (
                  stripeWebhooks.map((webhook) => (
                    <tr key={webhook.id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{webhook.event_type}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono truncate max-w-[12rem]">{webhook.event_id}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {webhook.updated_at ? (
                          <>
                            {formatDate(webhook.updated_at, 'medium')}
                            <span className="block text-xs text-gray-500">{formatTime(webhook.updated_at)}</span>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-red-700 max-w-xs truncate" title={webhook.error_message}>
                        {webhook.error_message || '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <RetryButton
                          disabled={isRetrying}
                          onClick={() => retryStripeWebhook.mutate(webhook.id)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {activeMeta?.last_page > 1 && (
        <Pagination
          currentPage={activeMeta.current_page}
          lastPage={activeMeta.last_page}
          perPage={activeMeta.per_page || perPage}
          total={activeMeta.total}
          onPageChange={handlePageChange}
          scrollToTop={false}
        />
      )}
    </div>
  );
};

export default AdminFailedOperations;
